"use client";

import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  ContentPagination,
  type OpenDataClient,
} from "../features/api/openData";
import type { RelatedArticleItem } from "@/src/components/news/RelatedArticleArea/RelatedArticleArea";
import { QUERY_KEYS } from "@config";
import { useOspApiClientProvider } from "@/src/hooks";

interface UseContentsOptions {
  initialPageParam: NonNullable<
    Parameters<OpenDataClient<unknown>["contents"]["getRankingContents"]>[0]
  >;
  initialData?: MaybeNullable<object & ContentPagination>;
  enabled?: boolean;
}

export const formatNewsRows = (rows: ContentPagination["rows"] = []) => {
  return rows.map((item) => ({
    ...item,
    time: item?.issue_date,
    title: item?.title,
    content: item?.content,
    tokens: item?.symbols?.map((symbol) => symbol?.symbol) as string[],
    sourceLink: item?.source_link,
    mediasImageUrl: item?.medias
      ?.filter((media) => media?.type === "image")
      ?.map((media) => media?.url)?.[0],
  }));
};

export type FormattedNewsRow = ReturnType<typeof formatNewsRows>[number];

/** Map useContents / formatNewsRows output to RelatedArticleArea items */
export const mapFormattedNewsRowsToRelatedArticles = (
  rows: FormattedNewsRow[],
): RelatedArticleItem[] => {
  return rows.map((row, index) => {
    const author = row.author;
    const displayAuthor = author?.display_name ?? author?.handle ?? "—";
    return {
      id: row.id ?? `related-article-${index}`,
      title: row.title ?? "",
      description:
        row.description ?? (row.content ? row.content.slice(0, 240) : "") ?? "",
      authorDisplayName: displayAuthor,
      date: row.time ?? row.issue_date ?? row.created ?? 0,
      imageUrl: row.mediasImageUrl ?? "/assets/images/image_place_holder.png",
      source: row.source_link,
      handle: author?.handle,
      avatar: author?.avatar,
    };
  });
};

export const useContents = (options: UseContentsOptions) => {
  const { openDataApi } = useOspApiClientProvider();
  const { initialPageParam, initialData, enabled = true } = options;
  // no local pageRef; rely on server pagination fields

  // Memoize queryKey to ensure it changes when params change
  const queryKey = useMemo(
    () => [QUERY_KEYS.LATEST_NEWS, initialPageParam],
    [initialPageParam],
  );

  const {
    data = [],
    isLoading,
    isFetchingNextPage,
    isFetching,
    isPending,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    enabled: !!openDataApi && enabled,
    queryKey,
    initialData: initialData
      ? {
          pages: [initialData],
          pageParams: [initialPageParam],
        }
      : undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryFn: async ({ pageParam }) => {
      const params = pageParam || initialPageParam;
      const res = await openDataApi?.contents.getRankingContents({
        ...params,
      });
      return res?.data?.data;
    },
    initialPageParam,
    getNextPageParam: (lastPage) => {
      // log.debug("[stock detail] getNextPageParam", lastPage);
      // return null
      const rowsLength = Number(lastPage?.rows?.length ?? 0);
      if (rowsLength === 0) {
        return null;
      }
      const lastOffset = Number(
        lastPage?.offset ?? initialPageParam?.offset ?? 0,
      );
      const total = Number(lastPage?.total ?? 0);

      const nextOffset = lastOffset + rowsLength; // offset 基于已返回的条数累加
      if (total && nextOffset >= total) {
        return null;
      }

      return {
        ...initialPageParam,
        offset: nextOffset,
      };
    },
    select(data) {
      return formatNewsRows(data?.pages.flatMap((page) => page?.rows || []));
    },
  });

  return {
    data,
    isLoading,
    isPending,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  };
};
